import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Shield, RefreshCw } from 'lucide-react';
import TopBar from '../../components/TopBar.jsx';
import { apiGet, apiPost } from '../../lib/api.js';

const VERDICT_OPTIONS = [
  { value: '', label: 'All verdicts' },
  { value: 'pass', label: 'Pass' },
  { value: 'soft_flag', label: 'Soft flag' },
  { value: 'block', label: 'Block' },
];

const VERDICT_STYLE = {
  pass: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  soft_flag: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  block: 'bg-red-500/10 text-red-300 border-red-500/30',
};

function VerdictPill({ verdict }) {
  const cls = VERDICT_STYLE[verdict] || 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30';
  return (
    <span className={`px-2 py-0.5 rounded-sm text-xs border ${cls}`}>{verdict}</span>
  );
}

function fmtTime(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function ComplianceLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [verdict, setVerdict] = useState('');
  const [loading, setLoading] = useState(false);

  const [previewMsg, setPreviewMsg] = useState('');
  const [previewState, setPreviewState] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (verdict) params.set('verdict', verdict);
      params.set('limit', '100');
      const r = await apiGet(`/compliance/checks?${params.toString()}`);
      if (r?.ok) {
        setItems(r.items || []);
        setTotal(r.total || 0);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load compliance log');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [verdict]);

  async function runPreview() {
    if (!previewMsg.trim()) {
      toast.error('Enter a message to preview');
      return;
    }
    setPreviewBusy(true);
    setPreviewResult(null);
    try {
      const r = await apiPost('/compliance/preview', {
        message: previewMsg,
        state: previewState || undefined,
      });
      if (r?.ok) {
        setPreviewResult(r);
      } else {
        toast.error(r?.error || 'Preview failed');
      }
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    } finally {
      setPreviewBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <TopBar title="Compliance Log" subtitle="Live pre-send compliance evaluations" icon={Shield} />

      {/* Preview-a-message form */}
      <div className="panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Preview a message</h3>
          <span className="text-xs text-zinc-400">Evaluates without sending</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px_auto] gap-2">
          <textarea
            value={previewMsg}
            onChange={(e) => setPreviewMsg(e.target.value)}
            placeholder="Hi {{firstName}}, quick note about your quote…"
            className="input min-h-[72px]"
          />
          <input
            value={previewState}
            onChange={(e) => setPreviewState(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="State (e.g. FL)"
            className="input"
          />
          <button onClick={runPreview} disabled={previewBusy} className="btn-primary">
            {previewBusy ? 'Checking…' : 'Preview'}
          </button>
        </div>
        {previewResult && (
          <div className="border border-zinc-700 rounded-sm p-3 bg-zinc-900/60 text-sm space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-zinc-400">Verdict:</span>
              <VerdictPill verdict={previewResult.verdict} />
              <span className="text-xs text-zinc-500">severity: {previewResult.severity}</span>
            </div>
            {!!(previewResult.flags || []).length && (
              <ul className="list-disc list-inside text-xs text-zinc-300">
                {previewResult.flags.map((f, i) => (
                  <li key={i}><b>{f.rule}</b>: {f.note}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="panel p-3 flex items-center gap-2">
        <select value={verdict} onChange={(e) => setVerdict(e.target.value)} className="input max-w-[180px]">
          {VERDICT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button onClick={load} className="btn-secondary flex items-center gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
        <div className="ml-auto text-xs text-zinc-400">{total} record{total === 1 ? '' : 's'}</div>
      </div>

      {/* Log table */}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-zinc-400 border-b border-zinc-800">
            <tr>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Verdict</th>
              <th className="px-3 py-2">Message preview</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2">Lead</th>
              <th className="px-3 py-2">Agent</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500">Loading…</td></tr>
            )}
            {!loading && !items.length && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-500">No compliance checks yet.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c._id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                <td className="px-3 py-2 whitespace-nowrap text-xs text-zinc-400">{fmtTime(c.createdAt)}</td>
                <td className="px-3 py-2"><VerdictPill verdict={c.verdict} /></td>
                <td className="px-3 py-2 max-w-[420px]">
                  <div className="truncate text-zinc-200">{c.messagePreview}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {(c.flags || []).length
                    ? (c.flags.map((f) => f.rule).join(', '))
                    : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {c.lead
                    ? `${c.lead.firstName || ''} ${c.lead.lastName || ''}`.trim() || c.lead.phone || c.lead._id
                    : <span className="text-zinc-500">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {c.agent ? (c.agent.name || c.agent.email) : <span className="text-zinc-500">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
